import { Button, CircularProgress } from '@mui/material'
import {
  StmtRecentResBodyPayloadStatementsInner as StatementItem,
  StmtAccountDetailsResBodyPayloadAccountHoldingsInner,
  StmtAccountDetailsResBodyPayloadNavDetailsInner,
  StmtContributionSummaryResBodyPayloadAccountHoldingsInner,
  StmtDocMsSearchResBodyPayloadListInner,
  StmtRecentResBodyPayloadAccountsListInner,
  StmtRecentResBodyPayloadStatementsInner,
  StmtRwpDetailsResBodyPayloadRwpDetailsInner,
  StmtTransactionDetailsResBodyPayloadTransactionLedgerInner,
} from '@pimy-b2cweb/apiclient-customer-stmt'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ButtonWithLoading } from '@pimy-b2cweb/frontend-lib'
import {
  useMultipleContributionQueries,
  useQueryStmtAccountDetails,
  useQueryStmtDistributorDetails,
  useQueryStmtRecent,
  useQueryStmtRspDetails,
  useQueryStmtRwpDetails,
  useQueryStmtSoaDocMsCifIdAccNoApi,
  useQueryStmtSoaDocMsSearchApi,
  useQueryStmtTransactionDetails,
} from '@/api/'
import { authSessionSlice } from '@/stores/auth'
import {
  AccountTrxTypeCategory,
  StatementAccountTypeCategory,
  StatementTypeCategory,
  contributionSummaryColumnMapping,
  accountDetailsColumnMapping,
  getErrorResponseCode,
  rwpDetailsColumnMapping,
  accountsType,
  CATEGORIZED_STATEMENT_TYPES,
  trxDetailsGhrPhrColumnMapping,
  trxDetailsNonGhrPhrColumnMapping,
} from '@/utils'
import useNotifications from '@/hooks/useNotifications/'
import Pagination from '../Pagination'
import TabSwitcher from '../TabSwitcher'
import DataTable from '../DataTable'
import StatementReport from '../StatementReport'
import ContributionDetails from '../ContributionDetails'
import { selectProfileSessionState } from '@/stores/profile.selectors'
import usePagination from '@/hooks/usePagination'
import RSPDetails from '../RSPDetails'
import DistributorDetails from '../DistributorDetails'
import RetirementCalculator from '../RetirementCalculator'
import { selectExistsInMs } from '@/stores/auth.selectors'

dayjs.extend(utc)
const { setIsUserIdle } = authSessionSlice.actions

export interface convertedStatementItemProps {
  month: string
  items: {
    dateStr: string
    items: StatementItem[]
  }[]
}

interface ExtendedContributionSummaryAccountHoldings extends StmtContributionSummaryResBodyPayloadAccountHoldingsInner {
  investmentApproach?: string
  isGHRPHR?: boolean
}

type validStatementTypeCategories =
  | StatementTypeCategory.UT
  | StatementTypeCategory.PRS
  | StatementTypeCategory.RETIREMENT_CALCULATOR

const getCategorizedStatements = (
  data: StatementItem[],
  selectedCategory: validStatementTypeCategories,
) => {
  const categorizedStatementTypes: string[] =
    CATEGORIZED_STATEMENT_TYPES[selectedCategory]
  return [
    ...data.filter((item) =>
      categorizedStatementTypes.includes(item.statementType),
    ),
  ]
}

export const Statements = () => {
  const { idNo, idType } = useSelector(selectProfileSessionState) || {}
  const existsInMs = useSelector(selectExistsInMs)
  const PAGE_SIZE = 10
  const [page, setPage] = useState(1)
  const visibleCount = page * PAGE_SIZE

  const { t } = useTranslation([
    'dashboardPage',
    'months',
    'statementTypeCategories',
    'statementTypes',
  ])

  const dispatch = useDispatch()
  const [selectedCategory, setSelectedCategory] =
    useState<validStatementTypeCategories>(StatementTypeCategory.UT)
  const [visibleCategories, setVisibleCategories] = useState<
    validStatementTypeCategories[]
  >([])

  const { data, status, error } = useQueryStmtRecent()
  const [accountNumber, setAccountNumber] = useState<string | null>(null)

  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>(
    StatementAccountTypeCategory.STATEMENT,
  )
  const [accountActiveTab, setAccountActiveTab] = useState<string | null>('')
  const [isAccountSectionVisiable, setIsAccountSectionVisiable] =
    useState<boolean>(false)
  const [trxDetailsColumnMapping, setTrxDetailsColumnMapping] = useState<
    Record<string, string>
  >({})

  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: 'asc' | 'desc'
  } | null>(null)

  const prsAccounts = useMemo(() => {
    const filtered =
      data?.accountsList?.filter(
        (acc: StmtRecentResBodyPayloadAccountsListInner) =>
          acc.accountType === accountsType.PRS_ACCOUNT,
      ) || []

    return filtered.map((acc: StmtRecentResBodyPayloadAccountsListInner) => ({
      accountNo: acc.accountNo!,
      idNo,
    }))
  }, [data?.accountsList, idNo])

  const contributionQueries = useMultipleContributionQueries(
    prsAccounts,
    !!prsAccounts.length && activeTab === StatementAccountTypeCategory.ACCOUNT,
  )

  const contributionData = useMemo<
    ExtendedContributionSummaryAccountHoldings[]
  >(() => {
    if (!Array.isArray(contributionQueries)) return []
    return contributionQueries?.flatMap((query, idx) => {
      if (!query || query.status !== 'success') return []

      const accountNo = Number(prsAccounts?.[idx]?.accountNo ?? 0)
      const investmentApproach = query.data?.investmentApproach
      const isGHRPHR = query.data?.isGHRPHR
      const accountHoldings = query.data?.accountHoldings

      if (Array.isArray(accountHoldings)) {
        return accountHoldings?.map((item) => ({
          ...item,
          accountNo,
          investmentApproach,
          isGHRPHR,
        }))
      }
      return []
    })
  }, [contributionQueries, prsAccounts])

  useEffect(() => {
    const categories: StatementTypeCategory[] = []
    if (existsInMs) {
      categories.push(StatementTypeCategory.UT)
    }
    categories.push(StatementTypeCategory.PRS)
    categories.push(StatementTypeCategory.RETIREMENT_CALCULATOR)

    setVisibleCategories(categories)

    if (categories.length > 0) {
      const preferredDefault = categories.find(
        (c) => c !== StatementTypeCategory.RETIREMENT_CALCULATOR,
      )
      setSelectedCategory(preferredDefault ?? categories[0])
    }
  }, [data?.accountsList, existsInMs])

  const { data: docMsCifIdAccNoApiResults } = useQueryStmtSoaDocMsCifIdAccNoApi(
    {
      country: 'MY',
      idType: idType!,
      idNo: Number(idNo),
    },
    {
      enabled: !!existsInMs && !!idNo && !!idType,
    },
  )

  const { data: docMsSearchApiResults, status: docMsSearchApiStatus } =
    useQueryStmtSoaDocMsSearchApi(
      {
        category: 0,
        cifId: docMsCifIdAccNoApiResults?.cifId,
        limit: PAGE_SIZE,
        page,
      },
      {
        enabled:
          !!existsInMs &&
          !!docMsCifIdAccNoApiResults?.cifId &&
          activeTab === StatementAccountTypeCategory.STATEMENT,
      },
    )

  const [accumulatedDocMsList, setAccumulatedDocMsList] = useState<
    StmtDocMsSearchResBodyPayloadListInner[]
  >([])
  const allQueriesSettled =
    docMsSearchApiStatus === 'success' || docMsSearchApiStatus === 'error'

  useEffect(() => {
    if (docMsSearchApiStatus !== 'loading' && allQueriesSettled) {
      const currentPageList: StmtDocMsSearchResBodyPayloadListInner[] =
        docMsSearchApiResults?.list ?? []

      setAccumulatedDocMsList((prev) => {
        const merged =
          page === 1 ? currentPageList : [...prev, ...currentPageList]
        return merged.sort((a, b) => {
          const da = new Date(a.docCreatedDate).getTime()
          const db = new Date(b.docCreatedDate).getTime()
          return db - da
        })
      })
    }
  }, [
    page,
    docMsSearchApiStatus,
    allQueriesSettled,
    docMsSearchApiResults?.list,
  ])

  const { data: accountDetailsResults, status: accountDetailsStatus } =
    useQueryStmtAccountDetails(
      {
        accountNo: accountNumber!,
        idNo,
      },
      {
        enabled:
          !!accountNumber &&
          accountActiveTab === AccountTrxTypeCategory.ACCOUNT_DETAILS,
      },
    )

  const { data: transactionDetailsResults, status: transactionDetailsStatus } =
    useQueryStmtTransactionDetails(
      {
        accountNo: accountNumber!,
        idType: idType || '',
        idNo,
      },
      {
        enabled:
          !!accountNumber &&
          accountActiveTab === AccountTrxTypeCategory.TRANSACTION_HISTORY,
      },
    )

  const { data: rspDetailsResults, status: rspDetailsStatus } =
    useQueryStmtRspDetails(
      {
        accountNo: accountNumber!,
        idNo,
      },
      {
        enabled:
          !!accountNumber && accountActiveTab === AccountTrxTypeCategory.RSP,
      },
    )

  const { data: rwpDetailsResults, status: rwpDetailsStatus } =
    useQueryStmtRwpDetails(
      {
        accountNo: accountNumber!,
        idNo,
      },
      {
        enabled:
          !!accountNumber && accountActiveTab === AccountTrxTypeCategory.RWP,
      },
    )

  const { data: distributorDetailsResults, status: distributorDetailsStatus } =
    useQueryStmtDistributorDetails(
      {
        accountNo: accountNumber!,
        idNo,
      },
      {
        enabled:
          !!accountNumber &&
          accountActiveTab === AccountTrxTypeCategory.DISTRIBUTOR_DETAILS,
      },
    )

  useEffect(() => {
    const isGHRPHR = transactionDetailsResults?.isGHRPHR
    if (isGHRPHR) {
      setTrxDetailsColumnMapping(trxDetailsGhrPhrColumnMapping)
    } else {
      setTrxDetailsColumnMapping(trxDetailsNonGhrPhrColumnMapping)
    }
  }, [transactionDetailsResults?.isGHRPHR])

  const handleCheckboxChange = (isChecked: boolean, index: number) => {
    setSelectedRowIndex(isChecked ? index : null)
    setIsAccountSectionVisiable(isChecked)
    if (isChecked && transformedPaginatedData().length > index) {
      const selectedAccountNo = transformedPaginatedData()[index]
      if (selectedAccountNo?.accountNo) {
        setAccountNumber(selectedAccountNo?.accountNo?.toString() || null)
      }
      setAccountActiveTab(AccountTrxTypeCategory.ACCOUNT_DETAILS)
    }
  }

  const transformedPaginatedData = () => {
    const groupedData: {
      [key: string]: StmtContributionSummaryResBodyPayloadAccountHoldingsInner
    } = {}
    contributionData?.forEach((item) => {
      const {
        accountNo,
        moneyType,
        totalContribution,
        totalContributionAmount,
        subAccountA,
        subAccountB,
        investmentApproach,
        isGHRPHR,
      } = item

      if (!accountNo) return

      if (!groupedData[accountNo]) {
        groupedData[accountNo] = {
          accountNo,
          investmentApproach: investmentApproach || 'Do-It-For-Me',
          subAccA: 0,
          subAccB: 0,
          totalContribution: 0,
          totalContributionAmount: 0,
          currentHoldingValue: 0,
          isGHRPHR,
        }
      }

      if (isGHRPHR) {
        if (moneyType === 'EMP-A' || moneyType === 'IND-A') {
          groupedData[accountNo].subAccA =
            Number(groupedData[accountNo].subAccA) +
            Number(totalContribution ?? 0)
        } else if (moneyType === 'EMP-B' || moneyType === 'IND-B') {
          groupedData[accountNo].subAccB =
            Number(groupedData[accountNo].subAccB) +
            Number(totalContribution ?? 0)
        }
        groupedData[accountNo].totalContribution! += Number(
          totalContribution ?? 0,
        )
        groupedData[accountNo].totalContributionAmount! += Number(
          totalContributionAmount ?? 0,
        )
      } else {
        groupedData[accountNo].subAccA = Number(subAccountA ?? 0)
        groupedData[accountNo].subAccB = Number(subAccountB ?? 0)
        groupedData[accountNo].totalContribution = Number(
          totalContribution ?? 0,
        )
        groupedData[accountNo].totalContributionAmount = Number(
          totalContributionAmount ?? 0,
        )
      }
    })
    return Object.values(groupedData)
  }

  const transformedAccountDetailsPaginatedData = useMemo(() => {
    if (
      !accountDetailsResults?.accountHoldings ||
      accountDetailsResults.accountHoldings.length === 0
    ) {
      return []
    }

    const groupedData: {
      [key: string]: StmtAccountDetailsResBodyPayloadAccountHoldingsInner
    } = {}
    accountDetailsResults.accountHoldings?.forEach(
      (item: StmtAccountDetailsResBodyPayloadAccountHoldingsInner) => {
        const {
          fundName,
          fundCode,
          totalWithdrawal,
          totalSwitchIn,
          totalSwitchOut,
          totalTransferOut,
          totalTransferIn,
          totalContributionAmount,
          totalUnit,
          navDate,
          isGHRPHR,
        } = item

        const key = fundName || fundCode
        if (!key) return

        if (!groupedData[key]) {
          groupedData[key] = {
            fundName,
            fundCode,
            totalWithdrawal: 0,
            totalSwitchIn: 0,
            totalSwitchOut: 0,
            totalTransferOut: 0,
            totalTransferIn: 0,
            totalContributionAmount: 0,
            totalUnit: 0,
            navDate,
            navPerUnit: 0,
          }
        }

        if (isGHRPHR && groupedData[key]) {
          groupedData[key].totalWithdrawal! += Number(totalWithdrawal ?? 0)
          groupedData[key].totalSwitchIn! += Number(totalSwitchIn ?? 0)
          groupedData[key].totalSwitchOut! += Number(totalSwitchOut ?? 0)
          groupedData[key].totalTransferOut! += Number(totalTransferOut ?? 0)
          groupedData[key].totalTransferIn! += Number(totalTransferIn ?? 0)
          groupedData[key].totalUnit! += Number(totalUnit ?? 0)
          // groupedData[key].navPerUnit += Number(navPerUnit) || 0
          groupedData[key].totalContributionAmount! += Number(
            totalContributionAmount ?? 0,
          )
        } else {
          groupedData[key].totalWithdrawal = Number(totalWithdrawal) || 0
          groupedData[key].totalSwitchIn = Number(totalSwitchIn) || 0
          groupedData[key].totalSwitchOut = Number(totalSwitchOut) || 0
          groupedData[key].totalTransferOut = Number(totalTransferOut) || 0
          groupedData[key].totalTransferIn = Number(totalTransferIn) || 0
          groupedData[key].totalUnit = Number(totalUnit) || 0
          // groupedData[key].navPerUnit = Number(navPerUnit) || 0
          groupedData[key].totalContributionAmount =
            Number(totalContributionAmount) || 0
        }
      },
    )
    return Object.values(groupedData)
  }, [accountDetailsResults?.accountHoldings])

  const accountDetailsMappedData = useMemo(() => {
    const groupedData = transformedAccountDetailsPaginatedData || []
    const navDetails = accountDetailsResults?.navDetails

    return groupedData.map((dataItem) => {
      const navItem = navDetails?.find(
        (nav: StmtAccountDetailsResBodyPayloadNavDetailsInner) =>
          nav.fundName === dataItem.fundName ||
          nav.fundCode === dataItem.fundCode,
      )

      return {
        ...dataItem,
        fundName: navItem ? navItem.fundName : dataItem.fundName,
        fundCode: dataItem.fundCode,
        navDate: navItem?.navDate,
        navPerUnit: Number(navItem?.navPerUnit ?? dataItem?.navPerUnit ?? 0),
      }
    })
  }, [
    transformedAccountDetailsPaginatedData,
    accountDetailsResults?.navDetails,
  ])

  const sortedTransactionDetails = useMemo(() => {
    if (
      !transactionDetailsResults?.transactionLedger ||
      !Array.isArray(transactionDetailsResults.transactionLedger)
    )
      return []
    if (!sortConfig) return transactionDetailsResults.transactionLedger

    const sorted = [...transactionDetailsResults.transactionLedger].sort(
      (a, b) => {
        const aValue =
          a[
            sortConfig.key as keyof StmtTransactionDetailsResBodyPayloadTransactionLedgerInner
          ]
        const bValue =
          b[
            sortConfig.key as keyof StmtTransactionDetailsResBodyPayloadTransactionLedgerInner
          ]

        if (aValue === undefined || bValue === undefined) return 0
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue)
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue
        }

        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue))
      },
    )
    return sorted
  }, [transactionDetailsResults?.transactionLedger, sortConfig])

  const handleTransactionSort = (header: string) => {
    const key = trxDetailsColumnMapping[header]
    if (!key) return

    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev?.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const rowsPerPage = 10
  const {
    currentPage,
    totalPages,
    handleNext,
    handlePrev,
    accountDetailsCurrentPage,
    accountDetailsTotalPages,
    handleAccountDetailsNext,
    handleAccountDetailsPrev,
    trxDetailsCurrentPage,
    trxDetailsTotalPages,
    handleTransactionHistoryNext,
    handleTransactionHistoryPrev,
    setCurrentPage,
    setAccountDetailsCurrentPage,
    setTrxDetailsCurrentPage,
    rwpDetailsCurrentPage,
    setRwpDetailsCurrentPage,
    rwpDetailsTotalPages,
    handleRWPDetailsNext,
    handleRWPDetailsPrev,
  } = usePagination({
    totalItems: contributionData?.length,
    rowsPerPage,
    intialPage: 1,
    accountDetailsTotalItems:
      transformedAccountDetailsPaginatedData?.length + 1,
    trxDetailsTotalItems: transactionDetailsResults?.transactionLedger?.length,
    rwpDetailsTotalItems: rwpDetailsResults?.length,
  })

  const accountDetailspaginatedData = useMemo(() => {
    if (!Array.isArray(accountDetailsMappedData)) return []

    const indexOfLastRow = accountDetailsCurrentPage * rowsPerPage
    const indexOfFirstRow = indexOfLastRow - rowsPerPage
    const currentRows = accountDetailsMappedData?.slice(
      indexOfFirstRow,
      indexOfLastRow,
    )
    return currentRows
  }, [accountDetailsMappedData, accountDetailsCurrentPage, rowsPerPage])

  const trxDetailsPaginatedData = useMemo(() => {
    if (!Array.isArray(sortedTransactionDetails)) return []

    const indexOfLastRow = trxDetailsCurrentPage * rowsPerPage
    const indexOfFirstRow = indexOfLastRow - rowsPerPage
    const currentRows =
      sortedTransactionDetails &&
      sortedTransactionDetails?.slice(indexOfFirstRow, indexOfLastRow)

    return currentRows
  }, [sortedTransactionDetails, trxDetailsCurrentPage, rowsPerPage])

  const rwpPaginatedData = useMemo(() => {
    if (!Array.isArray(rwpDetailsResults)) return []

    const indexOfLastRow = rwpDetailsCurrentPage * rowsPerPage
    const indexOfFirstRow = indexOfLastRow - rowsPerPage
    const currentRows = rwpDetailsResults?.slice(
      indexOfFirstRow,
      indexOfLastRow,
    )
    return currentRows
  }, [rwpDetailsResults, rwpDetailsCurrentPage, rowsPerPage])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setCurrentPage(1)
    setAccountDetailsCurrentPage(1)
    setTrxDetailsCurrentPage(1)
    setRwpDetailsCurrentPage(1)
  }

  const handleAccountTabChange = (tab: string) => {
    setAccountActiveTab(tab)
    setCurrentPage(1)
    setAccountDetailsCurrentPage(1)
    setTrxDetailsCurrentPage(1)
    setRwpDetailsCurrentPage(1)
  }

  const { addNetworkErrorNotice } = useNotifications()
  useEffect(() => {
    if (status === 'error') {
      const errMsg = getErrorResponseCode(error)
      console.error(errMsg)
      addNetworkErrorNotice()
      return
    }
  }, [status, data, error])

  const transformedDocMsList = (accumulatedDocMsList || []).map(
    (doc): StmtRecentResBodyPayloadStatementsInner => ({
      statementId: doc?.id ?? '',
      statementDate: doc?.docCreatedDate,
      statementType: doc?.type?.host_doc_category_name,
      statementUrl: '',
      accountNo: '',
      mobileNo: '',
      requestId: '',
      createdDt: '',
    }),
  )

  const statements = useMemo(
    () => getCategorizedStatements(data?.statements ?? [], selectedCategory),
    [data, selectedCategory],
  )

  useEffect(() => {
    setSelectedRowIndex(null)
  }, [activeTab])

  const _statements = useMemo(() => {
    const combinedStatements =
      selectedCategory === StatementTypeCategory.UT
        ? [...statements, ...transformedDocMsList]
        : [...statements]
    return convertStatement(combinedStatements, visibleCount)
  }, [statements, transformedDocMsList, selectedCategory, visibleCount])

  const isLoadingStatements =
    status === 'loading' || docMsSearchApiStatus === 'loading'
  const hasStatements = _statements.length > 0
  const fetchAttempted =
    status !== 'loading' || docMsSearchApiStatus !== 'loading'
  const showEmpty = fetchAttempted && !isLoadingStatements && !hasStatements

  const hasNoMoreStatements =
    (docMsSearchApiResults?.list?.length ?? 0) < PAGE_SIZE

  const handleLoadMore = () => {
    if (!hasNoMoreStatements) {
      setPage((p) => p + 1)
      dispatch(setIsUserIdle())
    }
  }

  return (
    <>
      <div className='mb-4'>
        <div
          className={` ${
            visibleCategories.length === 1 ? 'w-1/2 max-auto' : 'w-full'
          }  mb-6 h-10 flex justify-center`}
        >
          {visibleCategories.map((category) => (
            <Button
              key={category}
              onClick={() => {
                setSelectedCategory(category)
                setActiveTab(StatementAccountTypeCategory.STATEMENT)
              }}
              className={`flex-1 flex justify-center items-center rounded-full border-solid border-2 mr-2 last:mr-0 ${
                selectedCategory === category
                  ? 'border-pi-principal-blue bg-pi-sky-blue-1'
                  : 'border-pi-gray-2'
              }`}
            >
              <span
                className={`font-bold max-sm:text-sm text-base sm:text-lg ${
                  selectedCategory === category
                    ? 'text-pi-principal-blue'
                    : 'text-pi-gray-1'
                }`}
              >
                {t(category || '', { ns: 'statementTypeCategories' })}
              </span>
            </Button>
          ))}
        </div>
        {status === 'loading' ? (
          <div className='w-full h-[10vh] m-0 flex justify-center items-center'>
            <CircularProgress className='text-pi-principal-blue' />
          </div>
        ) : (
          <>
            <div className='p-4 bg-pi-gray-100 shadow-md rounded-md bg-[#f5f5f5]'>
              {[StatementTypeCategory.UT, StatementTypeCategory.PRS].includes(
                selectedCategory,
              ) &&
                activeTab !== StatementAccountTypeCategory.ACCOUNT && (
                  <p className='text-[#973195]'>
                    {t(
                      selectedCategory === StatementTypeCategory.PRS
                        ? 'prs-important-note'
                        : '',
                    )}
                  </p>
                )}

              {selectedCategory !==
                StatementTypeCategory.RETIREMENT_CALCULATOR && (
                <TabSwitcher
                  tabs={[
                    StatementAccountTypeCategory.STATEMENT,
                    StatementAccountTypeCategory.ACCOUNT,
                    StatementAccountTypeCategory.PASSWORD_GUIDELINE,
                  ]}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  selectedCategory={selectedCategory}
                />
              )}

              {activeTab === StatementAccountTypeCategory.STATEMENT &&
                (isLoadingStatements && existsInMs ? (
                  <div className='pt-4'>
                    <CircularProgress
                      size={20}
                      className='text-pi-principal-blue'
                    />
                  </div>
                ) : hasStatements ? (
                  <StatementReport _statements={_statements} />
                ) : showEmpty &&
                  !(
                    selectedCategory ===
                    StatementTypeCategory.RETIREMENT_CALCULATOR
                  ) ? (
                  <div className='pt-4'>
                    <span className='h-[10vh]'>{t('No item found')}</span>
                  </div>
                ) : null)}

              {activeTab === StatementAccountTypeCategory.ACCOUNT && (
                <>
                  <DataTable<
                    StmtContributionSummaryResBodyPayloadAccountHoldingsInner &
                      Record<string, unknown>
                  >
                    headers={Object.keys(contributionSummaryColumnMapping)}
                    columnMapping={
                      contributionSummaryColumnMapping as {
                        [
                          key: string
                        ]: keyof StmtContributionSummaryResBodyPayloadAccountHoldingsInner
                      }
                    }
                    rows={
                      (transformedPaginatedData() ||
                        []) as (StmtContributionSummaryResBodyPayloadAccountHoldingsInner &
                        Record<string, unknown>)[]
                    }
                    selectedRowIndex={selectedRowIndex}
                    handleAccountChecked={(isChecked, index) => {
                      handleCheckboxChange(isChecked, index)
                    }}
                    isCheckbox={true}
                    status={
                      contributionQueries.some((q) => q.status === 'loading')
                        ? 'loading'
                        : 'success'
                    }
                    category={StatementAccountTypeCategory.ACCOUNT}
                  />

                  {/* Pagination */}
                  {transformedPaginatedData().length > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onNext={handleNext}
                      onPrev={handlePrev}
                    />
                  )}
                </>
              )}

              {selectedCategory ===
                StatementTypeCategory.RETIREMENT_CALCULATOR && (
                <RetirementCalculator />
              )}
            </div>

            <div>
              {selectedRowIndex !== null &&
                isAccountSectionVisiable &&
                activeTab === StatementAccountTypeCategory.ACCOUNT && (
                  <div className='mt-8'>
                    <div className='flex gap-6'>
                      <TabSwitcher
                        tabs={[
                          AccountTrxTypeCategory.ACCOUNT_DETAILS,
                          AccountTrxTypeCategory.TRANSACTION_HISTORY,
                          AccountTrxTypeCategory.RSP,
                          AccountTrxTypeCategory.RWP,
                          AccountTrxTypeCategory.DISTRIBUTOR_DETAILS,
                        ]}
                        activeTab={accountActiveTab}
                        onTabChange={handleAccountTabChange}
                      />
                    </div>

                    <ContributionDetails
                      titleHeader={accountActiveTab}
                      accountActiveTab={accountActiveTab}
                      ytdContributionDetails={
                        accountDetailsResults?.AccountYtdContributonList
                      }
                    />

                    {accountActiveTab === AccountTrxTypeCategory.RSP && (
                      <RSPDetails
                        rspDetails={rspDetailsResults}
                        status={rspDetailsStatus}
                      />
                    )}

                    {accountActiveTab ===
                      AccountTrxTypeCategory.DISTRIBUTOR_DETAILS && (
                      <DistributorDetails
                        distributorDetails={distributorDetailsResults?.[0]}
                        status={distributorDetailsStatus}
                      />
                    )}

                    {accountActiveTab ===
                      AccountTrxTypeCategory.ACCOUNT_DETAILS && (
                      <>
                        <DataTable<
                          StmtAccountDetailsResBodyPayloadAccountHoldingsInner &
                            Record<string, unknown>
                        >
                          headers={Object.keys(accountDetailsColumnMapping)}
                          columnMapping={
                            accountDetailsColumnMapping as {
                              [
                                key: string
                              ]: keyof StmtAccountDetailsResBodyPayloadAccountHoldingsInner
                            }
                          }
                          rows={accountDetailspaginatedData || []}
                          selectedRowIndex={selectedRowIndex}
                          handleAccountChecked={handleCheckboxChange}
                          isCheckbox={false}
                          status={accountDetailsStatus}
                          category={AccountTrxTypeCategory.ACCOUNT_DETAILS}
                        />
                        {/* Pagination */}
                        {accountDetailspaginatedData.length > 0 && (
                          <Pagination
                            currentPage={accountDetailsCurrentPage}
                            totalPages={accountDetailsTotalPages}
                            onNext={handleAccountDetailsNext}
                            onPrev={handleAccountDetailsPrev}
                          />
                        )}
                      </>
                    )}
                    {accountActiveTab ===
                      AccountTrxTypeCategory.TRANSACTION_HISTORY && (
                      <>
                        <DataTable<
                          StmtTransactionDetailsResBodyPayloadTransactionLedgerInner &
                            Record<string, unknown>
                        >
                          headers={Object.keys(trxDetailsColumnMapping)}
                          columnMapping={
                            trxDetailsColumnMapping as {
                              [
                                key: string
                              ]: keyof StmtTransactionDetailsResBodyPayloadTransactionLedgerInner
                            }
                          }
                          rows={
                            (trxDetailsPaginatedData ||
                              []) as (StmtTransactionDetailsResBodyPayloadTransactionLedgerInner &
                              Record<string, unknown>)[]
                          }
                          selectedRowIndex={selectedRowIndex}
                          handleAccountChecked={handleCheckboxChange}
                          isCheckbox={false}
                          status={transactionDetailsStatus}
                          onSort={handleTransactionSort}
                          sortConfig={sortConfig}
                          category={AccountTrxTypeCategory.TRANSACTION_HISTORY}
                        />
                        {/* Pagination */}
                        {trxDetailsPaginatedData.length > 0 && (
                          <Pagination
                            currentPage={trxDetailsCurrentPage}
                            totalPages={trxDetailsTotalPages}
                            onNext={handleTransactionHistoryNext}
                            onPrev={handleTransactionHistoryPrev}
                          />
                        )}
                      </>
                    )}
                    {accountActiveTab === AccountTrxTypeCategory.RWP && (
                      <>
                        <DataTable<
                          StmtRwpDetailsResBodyPayloadRwpDetailsInner &
                            Record<string, unknown>
                        >
                          headers={Object.keys(rwpDetailsColumnMapping)}
                          columnMapping={
                            rwpDetailsColumnMapping as {
                              [
                                key: string
                              ]: keyof StmtRwpDetailsResBodyPayloadRwpDetailsInner
                            }
                          }
                          rows={
                            (rwpPaginatedData ||
                              []) as (StmtRwpDetailsResBodyPayloadRwpDetailsInner &
                              Record<string, unknown>)[]
                          }
                          selectedRowIndex={selectedRowIndex}
                          handleAccountChecked={handleCheckboxChange}
                          isCheckbox={false}
                          status={rwpDetailsStatus}
                          category={AccountTrxTypeCategory.RWP}
                        />
                        {/* Pagination */}
                        {rwpPaginatedData.length > 0 && (
                          <Pagination
                            currentPage={rwpDetailsCurrentPage}
                            totalPages={rwpDetailsTotalPages}
                            onNext={handleRWPDetailsNext}
                            onPrev={handleRWPDetailsPrev}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
            </div>
          </>
        )}
      </div>
      {activeTab === StatementAccountTypeCategory.STATEMENT &&
        selectedCategory !== StatementTypeCategory.RETIREMENT_CALCULATOR && (
          <ButtonWithLoading
            variant='outlined'
            size='large'
            className='w-full'
            onClick={handleLoadMore}
            disabled={isLoadingStatements || hasNoMoreStatements}
          >
            {t('Load more statement')}
          </ButtonWithLoading>
        )}
    </>
  )
}

const convertStatement = (
  statements: StatementItem[],
  visibleCount: number,
) => {
  const _statements: {
    month: string
    items: {
      dateStr: string
      items: StatementItem[]
    }[]
  }[] = []

  statements.slice(0, visibleCount).forEach((sItem) => {
    const date = dayjs.utc(sItem.statementDate).local()
    const month = date.format('MMMM')
    const dateStr = date.format('DD MMM YYYY')

    const _currMonth = _statements.find((mItem) => mItem.month === month)
    if (!_currMonth) {
      //append new month to _statements
      _statements.push({
        month,
        items: [
          {
            dateStr,
            items: [sItem],
          },
        ],
      })
    } else {
      const _currDate = _currMonth.items.find(
        (dItem) => dItem.dateStr === dateStr,
      )
      if (!_currDate) {
        //append new date to current month
        _currMonth.items.push({
          dateStr,
          items: [sItem],
        })
      } else {
        //append new item to current date
        _currDate.items.push(sItem)
      }
    }
  })

  return _statements
}

export default Statements
